import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import { usePropertyFilter } from '@/utils/usePropertyFilter';
import * as Sentry from '@sentry/react';
import '@/styles/ShortPostingInputPage.scss';

const ShortPostingInputPage = () => {
    const { apiFetch } = useAuth();
    const { accessiblePropertyIDs, isUnrestricted, isLoading: propertiesLoading } = usePropertyFilter();

    const todayStr = new Date().toISOString().split('T')[0];

    const [allProperties, setAllProperties] = useState([]);
    const [propertyID, setPropertyID] = useState('');
    const [occurredDate, setOccurredDate] = useState(todayStr);
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    // Fetch all active properties
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await apiFetch('/api/Property/all');
                const data = await response.json();
                if (data.success && Array.isArray(data.data)) {
                    const active = data.data.filter(p => p.active !== false);
                    setAllProperties(active);
                }
            } catch (e) {
                Sentry.captureException(e);
                toast.error('Failed to load properties.');
            }
        };
        fetchProperties();
    }, [apiFetch]);

    // Filter properties based on the user's access restrictions
    const visibleProperties = isUnrestricted
        ? allProperties
        : allProperties.filter(p => accessiblePropertyIDs?.includes(p.propertyID));

    // Set initial selected property once the visible list is resolved
    useEffect(() => {
        if (visibleProperties.length > 0 && !propertyID) {
            setPropertyID(visibleProperties[0].propertyID);
        }
    }, [visibleProperties, propertyID]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!occurredDate || !propertyID) {
            toast.error('Please fill in all required fields before submitting.');
            return;
        }

        setLoading(true);
        try {
            const response = await apiFetch('/api/Ops/ShortPosting', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    PropertyID: parseInt(propertyID),
                    OccurredDate: occurredDate,
                    Description: description || null,
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                toast.success('Short posting recorded successfully!');
                setOccurredDate(todayStr);
                setDescription('');
                if (visibleProperties.length > 0) setPropertyID(visibleProperties[0].propertyID);
            } else {
                toast.error(data.message || 'Failed to record short posting.');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('An error occurred while submitting the short posting.');
        } finally {
            setLoading(false);
        }
    };

    const isResolvingProperties = propertiesLoading || allProperties.length === 0;

    return (
        <div className="short-posting-input-page">
            <h1>Log Short Posting</h1>
            <form className="short-posting-form" onSubmit={handleSubmit}>

                <div className="form-group">
                    <label htmlFor="occurredDate">Date of Occurrence</label>
                    <input
                        type="date"
                        id="occurredDate"
                        value={occurredDate}
                        max={todayStr}
                        onChange={e => setOccurredDate(e.target.value)}
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="property">Property</label>
                    <select
                        id="property"
                        value={propertyID}
                        onChange={e => setPropertyID(e.target.value)}
                        required
                        disabled={isResolvingProperties}
                    >
                        {isResolvingProperties ? (
                            <option value="">Loading properties...</option>
                        ) : visibleProperties.length > 0 ? (
                            visibleProperties.map(p => (
                                <option key={p.propertyID} value={p.propertyID}>
                                    {p.propertyName}
                                </option>
                            ))
                        ) : (
                            <option value="">No properties assigned</option>
                        )}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Enter a description (optional)"
                    />
                </div>

                <div className="form-actions">
                    <button type="submit" className="btn-submit" disabled={loading || isResolvingProperties}>
                        {loading ? 'Submitting...' : 'Submit Short Posting'}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default ShortPostingInputPage;